var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { Animated, Image, Pressable, View } from 'react-native';
import { BLUE_500 } from '../../constants';
import { useBreakpoint } from '../../hooks';
import { InfoIcon } from '../../icons';
import { StateConfigContext } from '../../providers';
import { getTestId, isValidObject } from '../../utils';
import { Button } from '../button';
import { DynamicSvg } from '../dynamic-svg';
import { Health } from '../health';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { getCardTitleProps, getCardTitleStyles, getInterpolatedRotationValue, startRotationAnimation } from './CardTitleStyles';
export var CardTitle = props => {
  var stateContext = useContext(StateConfigContext);
  var cardTitleProps = getCardTitleProps(props, stateContext);
  var cardTitleStyles = getCardTitleStyles();
  var color = cardTitleProps.color,
    iconName = cardTitleProps.iconName,
    _cardTitleProps$image = cardTitleProps.imageProps,
    imageProps = _cardTitleProps$image === void 0 ? {} : _cardTitleProps$image,
    title = cardTitleProps.title,
    subTitle = cardTitleProps.subTitle,
    _cardTitleProps$actio = cardTitleProps.actionTypes,
    actionTypes = _cardTitleProps$actio === void 0 ? 'none' : _cardTitleProps$actio,
    actionAccessibilityProps = cardTitleProps.actionAccessibilityProps,
    actionIconName = cardTitleProps.actionIconName,
    actionCtaText = cardTitleProps.actionCtaText,
    _cardTitleProps$actio2 = cardTitleProps.actionCtaIcon,
    actionCtaIcon = _cardTitleProps$actio2 === void 0 ? 'RightChevronButtonIcon' : _cardTitleProps$actio2,
    actionCtaOnPress = cardTitleProps.actionCtaOnPress,
    showActionCtaWithCheveron = cardTitleProps.showActionCtaWithCheveron,
    actionHealthType = cardTitleProps.actionHealthType,
    infoIconTooltipProps = cardTitleProps.infoIconTooltipProps,
    actionIconShouldRotate = cardTitleProps.actionIconShouldRotate,
    showOnMountInfoIconTooltip = cardTitleProps.showOnMountInfoIconTooltip,
    _cardTitleProps$showI = cardTitleProps.showInfoIcon,
    showInfoIcon = _cardTitleProps$showI === void 0 ? false : _cardTitleProps$showI,
    infoIconPress = cardTitleProps.infoIconPress,
    isActionIconPressable = cardTitleProps.isActionIconPressable,
    onActionIconPress = cardTitleProps.onActionIconPress,
    showBulletSeperator = cardTitleProps.showBulletSeperator,
    _cardTitleProps$noTex = cardTitleProps.noTextTransform,
    noTextTransform = _cardTitleProps$noTex === void 0 ? false : _cardTitleProps$noTex,
    titleAccessibilityProps = cardTitleProps.titleAccessibilityProps,
    imageAccessibilityProps = cardTitleProps.imageAccessibilityProps,
    style = cardTitleProps.style,
    _cardTitleProps$testI = cardTitleProps.testID,
    testID = _cardTitleProps$testI === void 0 ? 'CardTitle' : _cardTitleProps$testI,
    restProps = __rest(cardTitleProps, ["color", "iconName", "imageProps", "title", "subTitle", "actionTypes", "actionAccessibilityProps", "actionIconName", "actionCtaText", "actionCtaIcon", "actionCtaOnPress", "showActionCtaWithCheveron", "actionHealthType", "infoIconTooltipProps", "actionIconShouldRotate", "showOnMountInfoIconTooltip", "showInfoIcon", "infoIconPress", "isActionIconPressable", "onActionIconPress", "showBulletSeperator", "noTextTransform", "titleAccessibilityProps", "imageAccessibilityProps", "style", "testID"]);
  var _useState = useState(showOnMountInfoIconTooltip),
    showTooltip = _useState[0],
    setShowTooltip = _useState[1];
  var animationRotation = useRef(new Animated.Value(0)).current;
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isSmallScreen = _useBreakpoint.isSmallScreen,
    isLargeScreen = _useBreakpoint.isLargeScreen;
  useEffect(() => {
    startRotationAnimation(animationRotation, actionIconShouldRotate);
  }, [actionIconShouldRotate]);
  var ActionIconComponent = () => {
    return _jsx(Animated.View, Object.assign({
      style: {
        transform: [{
          rotate: getInterpolatedRotationValue(animationRotation, cardTitleProps)
        }]
      }
    }, {
      children: !!actionIconName && _jsx(DynamicSvg, Object.assign({
        iconName: actionIconName,
        color: isActionIconPressable ? BLUE_500 : color
      }, getTestId(testID, actionIconName)))
    }));
  };
  var disableChildrenAccessibility = !isValidObject(actionAccessibilityProps);
  var COMPONENT = {
    cta: _jsx(_Fragment, {
      children: actionCtaText && _jsx(Button, Object.assign({
        buttonType: "tertiary",
        iconPosition: "right",
        title: actionCtaText,
        size: isSmallScreen ? 'sm' : 'md',
        iconName: showActionCtaWithCheveron && actionCtaIcon,
        onPress: actionCtaOnPress
      }, !disableChildrenAccessibility && {
        accessible: false,
        accessibilityElementsHidden: true,
        importantForAccessibility: 'no-hide-descendants'
      }, getTestId(testID, 'actionCta')))
    }),
    icon: _jsx(_Fragment, {
      children: !!actionIconName && (isActionIconPressable ? _jsx(Pressable, Object.assign({
        onPress: onActionIconPress,
        accessible: disableChildrenAccessibility
      }, {
        children: _jsx(ActionIconComponent, {})
      })) : _jsx(ActionIconComponent, {}))
    }),
    health: _jsx(_Fragment, {
      children: actionHealthType && _jsx(Health, Object.assign({
        type: actionHealthType,
        size: "sm"
      }, getTestId(testID, 'health')))
    })
  };
  return _jsxs(View, Object.assign({
    style: [cardTitleStyles.container, style]
  }, restProps, getTestId(testID), {
    accessible: false
  }, {
    children: [_jsxs(View, Object.assign({
      style: cardTitleStyles.iconImgContainer
    }, {
      children: [!!iconName && _jsx(DynamicSvg, Object.assign({
        iconName: iconName,
        style: cardTitleStyles.iconImg,
        color: color
      }, getTestId(testID, iconName))), isValidObject(imageProps) && _jsx(Image, Object.assign({
        source: imageProps,
        style: cardTitleStyles.iconImg
      }, getTestId(testID, 'image'), imageAccessibilityProps))]
    })), _jsxs(View, Object.assign({
      style: cardTitleStyles.content
    }, {
      children: [!!title && _jsx(Typography, Object.assign({
        style: cardTitleStyles.title,
        text: title,
        color: color,
        type: "title-single-line-" + (isSmallScreen ? 'lg' : 'xl'),
        noTextTransform: noTextTransform
      }, getTestId(testID, 'title'), titleAccessibilityProps)), !!subTitle && _jsx(Typography, Object.assign({
        text: title ? (showBulletSeperator ? '•' : '') + " " + subTitle : subTitle,
        color: color,
        type: "body-single-line-" + (isSmallScreen ? 'xs' : 'sm')
      }, getTestId(testID, 'subTitle'))), showInfoIcon && _jsx(Tooltip, Object.assign({
        position: isLargeScreen ? 'bottom-start' : 'bottom-end',
        offset: {
          x: -10,
          y: -6
        }
      }, infoIconTooltipProps, {
        isVisible: showTooltip,
        onClose: () => {
          if (infoIconTooltipProps === null || infoIconTooltipProps === void 0 ? void 0 : infoIconTooltipProps.autoHide) {
            setShowTooltip(false);
          }
        }
      }, getTestId(testID, 'showInfoIcon'), {
        children: _jsx(Pressable, Object.assign({
          onPress: () => {
            setShowTooltip(!showTooltip);
            infoIconPress();
          }
        }, {
          children: _jsx(InfoIcon, Object.assign({
            color: BLUE_500,
            fontSize: 16,
            style: cardTitleStyles.infoIcon
          }, getTestId(testID, 'infoIcon')))
        }))
      }))]
    })), actionTypes != 'none' && _jsx(View, Object.assign({
      style: cardTitleStyles.actionsContainer
    }, getTestId(testID, 'actionsContainer'), {
      accessible: !disableChildrenAccessibility
    }, actionAccessibilityProps, {
      children: COMPONENT[actionTypes]
    }))]
  }));
};